/**
 * Experiment Bucketing Utility
 *
 * Provides deterministic user bucketing for A/B experiments using cookies.
 * Works on both server-side (with context) and client-side (without context).
 */

import { v4 as uuid } from 'uuid';
import nookies from 'nookies';
import type {
  GetServerSidePropsContext,
  NextApiRequest,
  NextApiResponse,
} from 'next';
import type { ExperimentConfig, ExperimentVariant } from '@config/experiments';
import { isProd } from './predicates';

const EXPERIMENT_USER_ID_KEY = '__experiment_user_id__';
const EXPERIMENT_VARIANT_PREFIX = '__experiment_variant_';
const EXPERIMENT_VERSION_PREFIX = '__experiment_version_';
const EXPERIMENT_OVERRIDE_PREFIX = '__experiment_override_';

type NextApiContext = {
  req: NextApiRequest;
  res: NextApiResponse;
};

type IsomorphicContext = GetServerSidePropsContext | NextApiContext | undefined;

/**
 * Get or create a stable experiment user ID stored in a cookie
 *
 * @param ctx - Optional server-side context (undefined for client-side)
 * @returns A stable user ID for experiment bucketing
 */
export function getOrCreateExperimentUserId(ctx?: IsomorphicContext): string {
  const cookies = nookies.get(ctx);
  let userId = cookies[EXPERIMENT_USER_ID_KEY];

  if (!userId) {
    userId = uuid();
    nookies.set(ctx, EXPERIMENT_USER_ID_KEY, userId, {
      sameSite: 'lax',
      path: '/',
      secure: isProd,
      maxAge: 365 * 24 * 60 * 60, // 1 year
    });
  }

  return userId;
}

/**
 * Read the forced variant for an experiment, if one is set.
 *
 * Overrides are a local debugging aid, not a rollout mechanism: the value lives in a
 * cookie in one browser, so forcing a variant never changes what anyone else is
 * bucketed into. See `ExperimentsPanel`, which is how they get set.
 */
export function getExperimentOverride(
  ctx: IsomorphicContext,
  experimentId: string
): string | undefined {
  return nookies.get(ctx)[`${EXPERIMENT_OVERRIDE_PREFIX}${experimentId}`];
}

/**
 * Force an experiment to a variant for this browser.
 *
 * Deliberately session-scoped (no `maxAge`) — unlike the year-long bucketing cookies, an
 * override should not outlive the browser window. A forgotten one looks like a bug.
 */
export function setExperimentOverride(
  experimentId: string,
  variantValue: string
): void {
  nookies.set(
    undefined,
    `${EXPERIMENT_OVERRIDE_PREFIX}${experimentId}`,
    variantValue,
    {
      sameSite: 'lax',
      path: '/',
      secure: isProd,
    }
  );
}

/** Drop a forced variant, returning the browser to its real bucket. */
export function clearExperimentOverride(experimentId: string): void {
  nookies.destroy(undefined, `${EXPERIMENT_OVERRIDE_PREFIX}${experimentId}`, {
    path: '/',
  });
}

/**
 * Simple hash function to convert a string to a number
 * This ensures deterministic bucketing based on user ID
 */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Convert a user ID to a bucket number (0-99)
 * Same user ID + experiment ID always returns the same bucket
 */
function hashToBucket(userId: string, experimentId: string): number {
  const combined = `${userId}-${experimentId}`;
  const hash = hashString(combined);
  return hash % 100;
}

/**
 * Get the experiment variant for a user
 * Uses cookies to cache the result for consistency
 *
 * @param ctx - Optional server-side context (undefined for client-side)
 * @param experiment - Experiment configuration
 * @returns The variant ID the user is assigned to
 */
export function getExperimentVariant(
  ctx: IsomorphicContext,
  experiment: ExperimentConfig
): string {
  // Check if experiment is enabled
  if (!experiment.enabled) {
    return experiment.variants[0].value;
  }

  // A forced variant wins over both the cache and bucketing. Validated against the
  // current variants so a cookie left over from a renamed variant is ignored rather
  // than pinning the experiment to a value that no longer exists. Nothing is cached
  // here, so clearing the override drops straight back to the real bucket.
  const override = getExperimentOverride(ctx, experiment.id);

  if (
    override &&
    experiment.variants.some((v: ExperimentVariant) => v.value === override)
  ) {
    return override;
  }

  const cookies = nookies.get(ctx);

  // Check cookie for cached variant
  const cacheKey = `${EXPERIMENT_VARIANT_PREFIX}${experiment.id}`;
  const versionKey = `${EXPERIMENT_VERSION_PREFIX}${experiment.id}`;
  const cachedVariant = cookies[cacheKey];
  const cachedVersion = cookies[versionKey];

  // Check if cached variant is still valid AND version matches
  if (cachedVariant && cachedVersion === String(experiment.version)) {
    // Verify the cached variant is still valid
    const isValidVariant = experiment.variants.some(
      (v: ExperimentVariant) => v.value === cachedVariant
    );
    if (isValidVariant) {
      return cachedVariant;
    }
  }

  // Get or create user ID
  const userId = getOrCreateExperimentUserId(ctx);

  // Calculate bucket (0-99)
  const bucket = hashToBucket(userId, experiment.id);

  // Determine variant based on percentage allocation
  let cumulativePercentage = 0;
  let selectedVariant = experiment.variants[0].value;

  for (const variant of experiment.variants) {
    cumulativePercentage += variant.percentage;
    if (bucket < cumulativePercentage) {
      selectedVariant = variant.value;
      break;
    }
  }

  // Cache the result in cookies
  const cookieOptions = {
    sameSite: 'lax' as const,
    path: '/',
    secure: isProd,
    maxAge: 365 * 24 * 60 * 60, // 1 year
  };

  nookies.set(ctx, cacheKey, selectedVariant, cookieOptions);
  nookies.set(ctx, versionKey, String(experiment.version), cookieOptions);

  return selectedVariant;
}

/**
 * Clear all experiment cookies
 * Useful for testing or when a user opts out of experiments
 */
export function clearExperimentCookies(ctx?: IsomorphicContext): void {
  const cookies = nookies.get(ctx);
  const keysToRemove: string[] = [];

  // Find all experiment-related cookies
  Object.keys(cookies).forEach((key) => {
    if (
      key === EXPERIMENT_USER_ID_KEY ||
      key.startsWith(EXPERIMENT_VARIANT_PREFIX) ||
      key.startsWith(EXPERIMENT_VERSION_PREFIX) ||
      key.startsWith(EXPERIMENT_OVERRIDE_PREFIX)
    ) {
      keysToRemove.push(key);
    }
  });

  // Remove them
  keysToRemove.forEach((key) => {
    nookies.destroy(ctx, key, { path: '/' });
  });
}

/**
 * Clear every forced variant, leaving bucketing untouched.
 *
 * Distinct from `clearExperimentCookies`, which also drops the user ID and so
 * re-randomises which bucket you land in. "Reset all" in the panel wants to reveal your
 * real bucket, not hand you a new one.
 */
export function clearAllExperimentOverrides(ctx?: IsomorphicContext): void {
  Object.keys(nookies.get(ctx))
    .filter((key) => key.startsWith(EXPERIMENT_OVERRIDE_PREFIX))
    .forEach((key) => nookies.destroy(ctx, key, { path: '/' }));
}
